// Klub panel — adatréteg a mobilapp klub-sémájára építve:
// clubs/{clubId} (schedule.hours nyitvatartás), clubs/{clubId}/boards (order, state, ár),
// bookings (clubId, board = tábla sorszáma szövegként, startAt, durationMin, state).
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, onSnapshot, query, Timestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { toDate } from '../data';

export type BoardState = 'free' | 'closed' | 'maintenance' | 'archived';

export interface ClubBoard {
  id: string;
  order: number;
  name: string;
  location: string;
  state: BoardState;
  maintReason: string | null;
  closedReason: string;
  priceBase: number;
  priceMinutes: number;
  boardType: string;
}

export type BookingKind = 'cp' | 'friendly' | 'event' | 'manual';

export interface ClubBooking {
  id: string;
  board: number;
  start: Date;
  end: Date;
  durationMin: number;
  createdBy: string;
  participantIds: string[];
  guestName: string | null;
  state: 'pending' | 'confirmed' | 'cancelled';
  arrived: boolean;
  paid: boolean;
  kind: BookingKind;
  gameMode: string;
  raw: Record<string, unknown>;
}

export interface ClubInfo {
  id: string;
  name: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  boardType: string;
  hours: ({ from: string; to: string } | null)[]; // 0 = hétfő
  coverUrl: string | null;
  avatarUrl: string | null;
  raw: Record<string, unknown>;
}

export const DEFAULT_HOURS: ClubInfo['hours'] = [
  { from: '16:00', to: '23:00' }, { from: '16:00', to: '23:00' }, { from: '16:00', to: '23:00' },
  { from: '16:00', to: '23:00' }, { from: '15:00', to: '01:00' }, { from: '14:00', to: '01:00' }, null,
];
export const DAY_NAMES = ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'];

export const dayIndex = (d: Date) => (d.getDay() + 6) % 7; // hétfő = 0

const s = (v: unknown) => (typeof v === 'string' ? v : '');

export function clubFrom(id: string, v: Record<string, unknown>): ClubInfo {
  const sched = (v.schedule ?? {}) as { hours?: ClubInfo['hours'] };
  return {
    id, name: s(v.name) || 'Klub', city: s(v.city), address: s(v.address), phone: s(v.phone), email: s(v.email),
    boardType: s(v.boardType) || 'Steel tip',
    hours: Array.isArray(sched.hours) && sched.hours.length === 7 ? sched.hours : DEFAULT_HOURS,
    coverUrl: s(v.coverUrl) || null, avatarUrl: s(v.avatarUrl) || s(v.logoUrl) || null, raw: v,
  };
}

export function boardFrom(id: string, v: Record<string, unknown>): ClubBoard {
  const order = Number(v.order ?? 1);
  return {
    id, order, name: s(v.name) || `${order}. tábla`, location: s(v.location) || 'Főterem',
    state: (['free', 'closed', 'maintenance', 'archived'].includes(String(v.state)) ? v.state : 'free') as BoardState,
    maintReason: (v.maintReason as string) ?? null, closedReason: s(v.closedReason),
    priceBase: Number(v.priceBase ?? 4500), priceMinutes: Number(v.priceMinutes ?? 60), boardType: s(v.boardType),
  };
}

export function bookingFrom(id: string, v: Record<string, unknown>): ClubBooking {
  const start = toDate(v.startAt) ?? new Date(0);
  const dur = Number(v.durationMin ?? 60);
  const kind: BookingKind = v.eventId || v.kind === 'event' ? 'event'
    : v.rankedMatchId || v.matchId || v.ranked ? 'cp'
      : v.manual ? 'manual' : 'friendly';
  return {
    id, board: Number(v.board ?? 1) || 1, start, end: new Date(start.getTime() + dur * 60000), durationMin: dur,
    createdBy: s(v.createdBy), participantIds: (v.participantIds as string[]) ?? [], guestName: (v.guestName as string) ?? null,
    state: (v.state === 'cancelled' || v.state === 'pending' ? v.state : 'confirmed') as ClubBooking['state'],
    arrived: v.arrived === true, paid: v.onlinePaid === true || v.paidOnSite === true || v.paid === true,
    kind, gameMode: s(v.gameMode), raw: v,
  };
}

/** A nyitvatartás adott napra: kezdő és záró időpont Date-ként (éjfél utáni zárás a következő napra esik). */
export function openWindow(hours: ClubInfo['hours'], day: Date): { from: Date; to: Date } | null {
  const h = hours[dayIndex(day)];
  if (!h) return null;
  const [fh, fm] = h.from.split(':').map(Number);
  const [th, tm] = h.to.split(':').map(Number);
  const from = new Date(day.getFullYear(), day.getMonth(), day.getDate(), fh, fm || 0);
  let to = new Date(day.getFullYear(), day.getMonth(), day.getDate(), th === 24 ? 23 : th, th === 24 ? 59 : tm || 0);
  if (to <= from) to = new Date(to.getTime() + 864e5);
  return { from, to };
}

export const bookingPrice = (b: ClubBooking, boards: ClubBoard[]) => {
  if (typeof b.raw.totalPrice === 'number') return b.raw.totalPrice as number;
  const bd = boards.find((x) => x.order === b.board);
  return bd ? Math.round((bd.priceBase / Math.max(1, bd.priceMinutes)) * b.durationMin) : 0;
};

/* ---------- aktív klub kontextus ---------- */
interface Ctx {
  clubs: ClubInfo[] | null;
  club: ClubInfo | null;
  setClubId: (id: string) => void;
}
const ClubCtx = createContext<Ctx>({ clubs: null, club: null, setClubId: () => undefined });
export const useClub = () => useContext(ClubCtx);

const LS_KEY = 'dqClubPanelClubId';

export function ClubProvider({ children }: { children: React.ReactNode }) {
  const { user, isAdmin } = useAuth();
  const [clubs, setClubs] = useState<ClubInfo[] | null>(null);
  const [clubId, setClubIdState] = useState<string>(() => {
    try { return localStorage.getItem(LS_KEY) || ''; } catch { return ''; }
  });

  useEffect(() => {
    if (!user) return;
    // admin minden klubot lát (előnézet), a tulajdonos csak a sajátjait
    const q = isAdmin ? query(collection(db, 'clubs'), limit(300)) : query(collection(db, 'clubs'), where('ownerUid', '==', user.uid), limit(20));
    const unsub = onSnapshot(q, async (snap) => {
      let list = snap.docs.map((d) => clubFrom(d.id, d.data()));
      if (!isAdmin && !list.some((c) => c.id === user.uid)) {
        // a mobil-séma szerint a klub doksi-azonosítója a tulajdonos uid-ja
        const own = await getDocs(query(collection(db, 'clubs'), where('__name__', '==', user.uid))).catch(() => null);
        own?.docs.forEach((d) => list.push(clubFrom(d.id, d.data())));
      }
      list = list.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
      setClubs(list);
    }, () => setClubs([]));
    return unsub;
  }, [user, isAdmin]);

  const club = useMemo(() => clubs?.find((c) => c.id === clubId) ?? clubs?.[0] ?? null, [clubs, clubId]);
  const setClubId = (id: string) => {
    setClubIdState(id);
    try { localStorage.setItem(LS_KEY, id); } catch { /* nincs tárhely */ }
  };
  return <ClubCtx.Provider value={{ clubs, club, setClubId }}>{children}</ClubCtx.Provider>;
}

/* ---------- élő adat-hookok ---------- */
export function useBoards(clubId: string | undefined) {
  const [boards, setBoards] = useState<ClubBoard[] | null>(null);
  useEffect(() => {
    if (!clubId) return;
    setBoards(null);
    return onSnapshot(collection(db, 'clubs', clubId, 'boards'),
      (sn) => setBoards(sn.docs.map((d) => boardFrom(d.id, d.data())).filter((b) => b.state !== 'archived').sort((a, b) => a.order - b.order)),
      () => setBoards([]));
  }, [clubId]);
  return boards;
}

/** A klub foglalásai egy időablakban (alapból: 35 nappal ezelőttől 35 napra előre). */
export function useBookings(clubId: string | undefined, fromDays = 35, toDays = 35) {
  const [list, setList] = useState<ClubBooking[] | null>(null);
  useEffect(() => {
    if (!clubId) return;
    setList(null);
    const from = Timestamp.fromDate(new Date(Date.now() - fromDays * 864e5));
    return onSnapshot(query(collection(db, 'bookings'), where('clubId', '==', clubId), limit(1500)),
      (sn) => setList(sn.docs.map((d) => bookingFrom(d.id, d.data()))
        .filter((b) => b.start.getTime() >= from.toMillis() && b.start.getTime() <= Date.now() + toDays * 864e5)
        .sort((a, b) => a.start.getTime() - b.start.getTime())),
      () => setList([]));
  }, [clubId, fromDays, toDays]);
  return list;
}

/** Felhasználónevek gyorsítótárral (foglalások résztvevőihez). */
const nameCache = new Map<string, { name: string; avatar: string | null; cp: number }>();
export function usePeople(uids: string[]) {
  const key = Array.from(new Set(uids.filter(Boolean))).sort().join(',');
  const [, force] = useState(0);
  useEffect(() => {
    const missing = key.split(',').filter((u) => u && !nameCache.has(u));
    if (!missing.length) return;
    let alive = true;
    (async () => {
      for (let i = 0; i < missing.length; i += 10) {
        const chunk = missing.slice(i, i + 10);
        const sn = await getDocs(query(collection(db, 'users'), where('__name__', 'in', chunk))).catch(() => null);
        sn?.docs.forEach((d) => {
          const v = d.data();
          nameCache.set(d.id, { name: v.username || v.displayName || 'Játékos', avatar: v.avatarUrl || v.photoURL || null, cp: Number(v.dqScore ?? 0) });
        });
        chunk.forEach((u) => { if (!nameCache.has(u)) nameCache.set(u, { name: 'Játékos', avatar: null, cp: 0 }); });
      }
      if (alive) force((x) => x + 1);
    })();
    return () => { alive = false; };
  }, [key]);
  return (uid: string) => nameCache.get(uid) ?? { name: '…', avatar: null, cp: 0 };
}

export const bookingTitle = (b: ClubBooking, who: (u: string) => { name: string }) =>
  b.guestName || who(b.createdBy).name;

